import React, { useState, useEffect } from 'react'
import { Modal, Table, Radio, Button, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import { SettingOutlined } from '@ant-design/icons'

const CONFIG_KEY = 'picking_list_generate_config'

export const DEFAULT_CONFIG = {
  taskType: 'primary',
  salesChannelIds: 'primary',
  dateRange: 'primary',
  shippingTypeIds: 'primary',

  // Defaults to advanced
  containsProducts: 'advanced',
  orderNames: 'advanced',
  productsCount: 'advanced',
  totalquantityCount: 'advanced',
  sameProductFilter: 'advanced',
  noteFilter: 'advanced',
  urgent: 'advanced',
  fulfillmentsLimit: 'primary'
}

export default function SearchFormConfigModal({ onConfigChange }) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const [config, setConfig] = useState(DEFAULT_CONFIG)

  // Load config from local storage
  useEffect(() => {
    const savedConfig = localStorage.getItem(CONFIG_KEY)
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig)
        // Merge with default config to handle new fields
        // And filter out keys that are not in DEFAULT_CONFIG (cleanup old keys)
        const merged = { ...DEFAULT_CONFIG }
        Object.keys(parsed).forEach(key => {
          if (Object.prototype.hasOwnProperty.call(DEFAULT_CONFIG, key)) {
            merged[key] = parsed[key]
          }
        })
        setConfig(merged)
        onConfigChange?.(merged)
      } catch (e) {
        console.error('Failed to parse saved config', e)
      }
    } else {
      onConfigChange?.(DEFAULT_CONFIG)
    }
  }, [])

  const handleSave = () => {
    localStorage.setItem(CONFIG_KEY, JSON.stringify(config))
    onConfigChange?.(config)
    setVisible(false)
  }

  const handleReset = () => {
    setConfig(DEFAULT_CONFIG)
  }

  const columns = [
    {
      title: t('common.table.headers.name'),
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: t('shared.picking_lists.generate.field_config.primary'),
      key: 'primary',
      width: 100,
      render: (_, record) => (
        <Radio
          checked={config[record.key] === 'primary'}
          onChange={() => setConfig(prev => ({ ...prev, [record.key]: 'primary' }))}
        />
      )
    },
    {
      title: t('shared.picking_lists.generate.field_config.advanced'),
      key: 'advanced',
      width: 100,
      render: (_, record) => (
        <Radio
          checked={config[record.key] === 'advanced'}
          onChange={() => setConfig(prev => ({ ...prev, [record.key]: 'advanced' }))}
        />
      )
    },
    {
      title: t('shared.picking_lists.generate.field_config.hidden'),
      key: 'hidden',
      width: 100,
      render: (_, record) => (
        <Radio
          checked={config[record.key] === 'hidden'}
          onChange={() => setConfig(prev => ({ ...prev, [record.key]: 'hidden' }))}
        />
      )
    }
  ]

  const fields = [
    { key: 'taskType', label: t('shared.picking_lists.generate.filters.task_type') },
    { key: 'salesChannelIds', label: t('models.sales_channel.name') },
    { key: 'dateRange', label: t('shared.picking_lists.generate.filters.date_range') },
    { key: 'shippingTypeIds', label: t('models.order.shipping_type') },
    { key: 'containsProducts', label: t('shared.picking_lists.generate.filters.contains_products') },
    { key: 'orderNames', label: t('models.order.order_number') },
    { key: 'productsCount', label: t('models.order.products_count') },
    { key: 'totalquantityCount', label: t('models.order.total_quantity_count') },
    { key: 'sameProductFilter', label: t('shared.picking_lists.generate.filters.same_product_filter') },
    { key: 'noteFilter', label: t('shared.picking_lists.generate.filters.note_filter') },
    { key: 'urgent', label: t('models.order.urgent') },
    { key: 'fulfillmentsLimit', label: t('shared.picking_lists.generate.filters.fulfillments_limit') },
  ]

  return (
    <>
      <Button
        type="text"
        icon={<SettingOutlined />}
        onClick={() => setVisible(true)}
      />

      <Modal
        title={t('shared.picking_lists.generate.field_config.title')}
        open={visible}
        onOk={handleSave}
        onCancel={() => setVisible(false)}
        width={600}
        footer={[
          <Button key="reset" onClick={handleReset}>
            {t('common.button.reset')}
          </Button>,
          <Button key="cancel" onClick={() => setVisible(false)}>
            {t('common.button.cancel')}
          </Button>,
          <Button key="submit" type="primary" onClick={handleSave}>
            {t('common.button.confirm')}
          </Button>,
        ]}
      >
        <Table
          dataSource={fields}
          columns={columns}
          rowKey="key"
          pagination={false}
          size="small"
          scroll={{ y: 400 }}
        />
      </Modal>
    </>
  )
}
